import type { FC, MouseEvent } from 'react';
import React, { useCallback, useContext, useEffect, useRef, Fragment } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import StarUnfilledIcon from '@atlaskit/icon/core/star-unstarred';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { StarSelectedIcon } from '@confluence/icons/entry-points/StarSelectedIcon.ts';
import { getConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getConfluenceContentAri.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';

import { PageCardContext } from '../../PageCardContext.ts';
import { CustomAppearanceButton } from '../PresentationalComponents.tsx';
import { usePageCardAnalyticsEvents } from '../../hooks/index.ts';
import type { Content } from '../../card-types.ts';

import type {
	StarButtonPageCardUnstarMutation as StarButtonPageCardUnstarMutation$data,
	StarButtonPageCardUnstarMutationVariables as StarButtonPageCardUnstarMutation$variables,
} from './__types__/StarButtonPageCardUnstarMutation.ts';
import type {
	StarButtonPageCardStarMutation as StarButtonPageCardStarMutation$data,
	StarButtonPageCardStarMutationVariables as StarButtonPageCardStarMutation$variables,
} from './__types__/StarButtonPageCardStarMutation.ts';
import type { StarButtonPageCardStarFragment$data } from './__generated__/StarButtonPageCardStarFragment.graphql.ts';
import StarButtonPageCardStarFragment_artifact from './__generated__/StarButtonPageCardStarFragment.graphql.ts';
import type { StarButtonBlogPostCardStarFragment$data } from './__generated__/StarButtonBlogPostCardStarFragment.graphql.ts';
import StarButtonBlogPostCardStarFragment_artifact from './__generated__/StarButtonBlogPostCardStarFragment.graphql.ts';
import type { StarButtonWhiteboardCardStarFragment$data } from './__generated__/StarButtonWhiteboardCardStarFragment.graphql.ts';
import StarButtonWhiteboardCardStarFragment_artifact from './__generated__/StarButtonWhiteboardCardStarFragment.graphql.ts';
import type { StarButtonDatabaseCardStarFragment$data } from './__generated__/StarButtonDatabaseCardStarFragment.graphql.ts';
import StarButtonDatabaseCardStarFragment_artifact from './__generated__/StarButtonDatabaseCardStarFragment.graphql.ts';
import type { StarButtonEmbedCardStarFragment$data } from './__generated__/StarButtonEmbedCardStarFragment.graphql.ts';
import StarButtonEmbedCardStarFragment_artifact from './__generated__/StarButtonEmbedCardStarFragment.graphql.ts';
import type { StarButtonFolderCardStarFragment$data } from './__generated__/StarButtonFolderCardStarFragment.graphql.ts';
import StarButtonFolderCardStarFragment_artifact from './__generated__/StarButtonFolderCardStarFragment.graphql.ts';
// eslint-disable-next-line import/no-cycle
import { ActionContainer } from './ActionContainer.tsx';
import { getCapitalizedContentType } from './starButtonUtils.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
const StarButtonPageCardStarMutation = graphql`
	mutation StarButtonPageCardStarMutation($id: ID!) {
		favouritePage(favouritePageInput: { pageId: $id }) {
			content {
				id
				metadata {
					currentuser {
						favourited {
							isFavourite
						}
					}
				}
			}
		}
	}
`;
const StarButtonPageCardUnstarMutation = graphql`
	mutation StarButtonPageCardUnstarMutation($id: ID!) {
		unfavouritePage(favouritePageInput: { pageId: $id }) {
			content {
				id
				metadata {
					currentuser {
						favourited {
							isFavourite
						}
					}
				}
			}
		}
	}
`;
const i18n = defineMessages({
	star: {
		id: 'page-card.star.content.tooltip.label',
		defaultMessage: 'Star',
		description:
			'label for unfilled star icon tooltip which indicates that the content is unstarred & stars the content when clicked',
	},
	unstar: {
		id: 'page-card.unstar.content.tooltip.label',
		defaultMessage: 'Unstar',
		description:
			'label for filled star icon tooltip which indicates that the content is starred & unstars the content when clicked',
	},
});

export const StarButton: FC = () => {
	const { id, type, isStarred, appearance } = useContext(PageCardContext);
	const intl = useIntl();
	const compact = appearance === 'compact-list';
	const { createAnalyticsEvent } = usePageCardAnalyticsEvents();
	const { activationId, cloudId } = useSessionData();
	const writePageStarButtonFragment = useWriteFragmentInterop<
		Omit<StarButtonPageCardStarFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: StarButtonPageCardStarFragment_artifact,
		fragmentSpec: graphql`
			fragment StarButtonPageCardStarFragment on ConfluencePage {
				viewer {
					favoritedSummary {
						isFavorite
					}
				}
			}
		`,
	});
	const writeBlogPostStarButtonFragment = useWriteFragmentInterop<
		Omit<StarButtonBlogPostCardStarFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: StarButtonBlogPostCardStarFragment_artifact,
		fragmentSpec: graphql`
			fragment StarButtonBlogPostCardStarFragment on ConfluenceBlogPost {
				viewer {
					favoritedSummary {
						isFavorite
					}
				}
			}
		`,
	});
	const writeWhiteboardStarButtonFragment = useWriteFragmentInterop<
		Omit<StarButtonWhiteboardCardStarFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: StarButtonWhiteboardCardStarFragment_artifact,
		fragmentSpec: graphql`
			fragment StarButtonWhiteboardCardStarFragment on ConfluenceWhiteboard {
				viewer {
					favoritedSummary {
						isFavorite
					}
				}
			}
		`,
	});
	const writeDatabaseStarButtonFragment = useWriteFragmentInterop<
		Omit<StarButtonDatabaseCardStarFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: StarButtonDatabaseCardStarFragment_artifact,
		fragmentSpec: graphql`
			fragment StarButtonDatabaseCardStarFragment on ConfluenceDatabase {
				viewer {
					favoritedSummary {
						isFavorite
					}
				}
			}
		`,
	});
	const writeEmbedStarButtonFragment = useWriteFragmentInterop<
		Omit<StarButtonEmbedCardStarFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: StarButtonEmbedCardStarFragment_artifact,
		fragmentSpec: graphql`
			fragment StarButtonEmbedCardStarFragment on ConfluenceEmbed {
				viewer {
					favoritedSummary {
						isFavorite
					}
				}
			}
		`,
	});
	const writeFolderStarButtonFragment = useWriteFragmentInterop<
		Omit<StarButtonFolderCardStarFragment$data, ' $fragmentType'>
	>({
		fragmentArtifact: StarButtonFolderCardStarFragment_artifact,
		fragmentSpec: graphql`
			fragment StarButtonFolderCardStarFragment on ConfluenceFolder {
				viewer {
					favoritedSummary {
						isFavorite
					}
				}
			}
		`,
	});
	const [star, { error: starError }] = useMutationInterop<
		StarButtonPageCardStarMutation$data,
		StarButtonPageCardStarMutation$variables
	>(StarButtonPageCardStarMutation);
	const [unstar, { error: unstarError }] = useMutationInterop<
		StarButtonPageCardUnstarMutation$data,
		StarButtonPageCardUnstarMutation$variables
	>(StarButtonPageCardUnstarMutation);

	// Toggling `isStarred` swaps between two structurally different button elements (the unstarred
	// variant is wrapped in an ActionContainer, the starred one is not), so React unmounts and
	// remounts the button and keyboard focus is lost. When the user activates the button we flag
	// that focus should be restored, then move focus back to the freshly mounted button once the
	// starred state has changed. See A11Y-30578.
	const buttonRef = useRef<HTMLElement>(null);
	const shouldRestoreFocusRef = useRef(false);

	useEffect(() => {
		if (shouldRestoreFocusRef.current) {
			shouldRestoreFocusRef.current = false;
			buttonRef.current?.focus();
		}
	}, [isStarred]);

	const sendAnalyticsEvent = useCallback(
		(action: string) => {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action,
					actionSubject: 'favouritePage',
					attributes: {
						type,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, type],
	);

	const modifyStarCache = useCallback(
		({
			newStarredValue,
			useAggClient = false,
		}: {
			newStarredValue: boolean;
			useAggClient?: boolean;
		}) => {
			const capitalizedType = getCapitalizedContentType(type);
			const dataTypename = `Confluence${capitalizedType}`;

			// The PageCardStar/PageCardUnstar mutations do not return the updated viewer data, so we need to manually update the cache.
			// This is necessary to update the UI immediately after the user clicks the star button.

			const starButtonRelayFragments: Record<Content, any> = {
				page: writePageStarButtonFragment,
				blogpost: writeBlogPostStarButtonFragment,
				whiteboard: writeWhiteboardStarButtonFragment,
				slide: writeWhiteboardStarButtonFragment,
				database: writeDatabaseStarButtonFragment,
				embed: writeEmbedStarButtonFragment,
				folder: writeFolderStarButtonFragment,
			};

			const writeStarButtonFragment = (type: Content) => starButtonRelayFragments[type];

			writeStarButtonFragment(type)({
				fragmentId: `${dataTypename}:${getConfluenceContentAri({ siteId: cloudId, resourceType: type, activationId, resourceId: id })}`,
				useAggClient,
				data: {
					viewer: {
						favoritedSummary: {
							isFavorite: newStarredValue,
							__typename: 'ConfluenceFavoritedSummary',
						},
						__typename: `Confluence${capitalizedType}ViewerSummary`,
					},
					__typename: dataTypename,
				},
			});
		},
		[
			id,
			type,
			activationId,
			cloudId,
			writePageStarButtonFragment,
			writeBlogPostStarButtonFragment,
			writeWhiteboardStarButtonFragment,
			writeDatabaseStarButtonFragment,
			writeEmbedStarButtonFragment,
			writeFolderStarButtonFragment,
		],
	);

	const onStarClick = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			shouldRestoreFocusRef.current = true;
			void star({
				variables: { id },
				optimisticResponse: {
					favouritePage: {
						__typename: 'FavouritePagePayload',
						content: {
							__typename: 'Content',
							id,
							metadata: {
								__typename: 'ContentMetadata',
								currentuser: {
									__typename: 'ContentMetadata_CurrentUserMetadataProvider_currentuser',
									favourited: {
										// @ts-expect-error __typename is not present in Relay generated types
										__typename: 'FavouritedSummary',
										isFavourite: true,
									},
								},
							},
						},
					},
				},
			});
			sendAnalyticsEvent('created');
			modifyStarCache({ newStarredValue: true, useAggClient: true });
		},
		[star, id, sendAnalyticsEvent, modifyStarCache],
	);

	const onUnstarClick = useCallback(
		(e: MouseEvent) => {
			e.stopPropagation();
			shouldRestoreFocusRef.current = true;
			void unstar({
				variables: { id },
				optimisticResponse: {
					unfavouritePage: {
						__typename: 'FavouritePagePayload',
						content: {
							__typename: 'Content',
							id,
							metadata: {
								__typename: 'ContentMetadata',
								currentuser: {
									// @ts-expect-error __typename is not present in Relay generated types but present in response
									__typename: 'ContentMetadata_CurrentUserMetadataProvider_currentuser',
									favourited: null,
								},
							},
						},
					},
				},
			});
			sendAnalyticsEvent('deleted');
			modifyStarCache({ newStarredValue: false, useAggClient: true });
		},
		[unstar, id, sendAnalyticsEvent, modifyStarCache],
	);

	const tooltipLabel = isStarred ? intl.formatMessage(i18n.unstar) : intl.formatMessage(i18n.star);

	return (
		<Fragment>
			{starError && <ErrorDisplay error={starError} />}
			{unstarError && <ErrorDisplay error={unstarError} />}
			<Tooltip content={tooltipLabel} position="top" hideTooltipOnClick>
				<div>
					{isStarred ? (
						<CustomAppearanceButton
							ref={buttonRef}
							id={`star-button-${id}`}
							spacing={compact ? 'compact' : 'default'}
							appearance="subtle"
							onClick={onUnstarClick}
							aria-label={intl.formatMessage(i18n.star)}
							aria-pressed={isStarred}
							aria-labelledby={`star-button-${id} content-title-${id}`}
							iconBefore={<StarSelectedIcon label="" />}
							testId="page-unstar-button"
						/>
					) : (
						<ActionContainer>
							<CustomAppearanceButton
								ref={buttonRef}
								id={`star-button-${id}`}
								spacing={compact ? 'compact' : 'default'}
								appearance="subtle"
								onClick={onStarClick}
								aria-label={intl.formatMessage(i18n.star)}
								aria-pressed={isStarred}
								aria-labelledby={`star-button-${id} content-title-${id}`}
								iconBefore={<StarUnfilledIcon label="" />}
								testId="page-star-button"
							/>
						</ActionContainer>
					)}
				</div>
			</Tooltip>
		</Fragment>
	);
};
